import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Shield, Award, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { whatsappLinks } from "@/lib/whatsapp";

const STATS = [
  { v: "60 min", l: "Express Reparatur" },
  { v: "6 Mo.", l: "Garantie" },
  { v: "OEM & Original", l: "Ersatzteile" },
  { v: "A1 + MTel", l: "Partnerbetrieb" },
];

export default function Hero() {
  return (
    <section className="relative pt-20 pb-20 md:pt-24 md:pb-28 overflow-hidden bg-luxury" data-testid="hero-section">
      <div className="absolute inset-0 dot-bg opacity-50 pointer-events-none"></div>
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#DC1928]/12 blur-[120px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs backdrop-blur">
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#FF2D40] font-bold">Erleben, was verbindet.</span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-[88px] font-bold tracking-tight text-white mt-5 leading-[0.98]">
              Alles<br/>
              rund um<br/>
              <span className="bg-gradient-to-r from-[#FF2D40] via-[#DC1928] to-[#FF2D40] bg-clip-text text-transparent">Handys!</span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-xl">
              Reparaturen, Service, Tarifberatung, Wertkarten &amp; Zubehör.<br/>
              <span className="font-medium text-white">Alles in einer Hand.</span>
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/reparaturen#configurator">
                <Button className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 px-7 text-base btn-lift glow-red" data-testid="hero-repair-cta">
                  Reparatur anfragen <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/ankauf">
                <Button variant="outline" className="rounded-full h-12 px-7 text-base bg-white/5 border-[#DC1928]/40 text-white hover:bg-[#DC1928]/10 hover:text-white hover:border-[#DC1928]/70 btn-lift" data-testid="hero-buyback-cta">
                  <Sparkles className="w-4 h-4 mr-2 text-[#FF2D40]" />
                  Handy verkaufen
                </Button>
              </Link>
              <a href={whatsappLinks.generic().href} onClick={whatsappLinks.generic().onClick} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full h-12 px-7 text-base bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white btn-lift" data-testid="hero-whatsapp-cta">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2 fill-[#25D366]"><path d="M17.498 14.382c-.301-.15-1.767-.872-2.04-.972-.273-.1-.472-.15-.672.15-.197.298-.771.97-.944 1.17-.174.2-.347.226-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.501.099-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.18 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.225-.57-.345m-5.446 7.443h-.016a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp Kontakt
                </Button>
              </a>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              {STATS.map((t, i) => (
                <div key={i} className="border-l border-[#DC1928]/60 pl-4" data-testid={`trust-badge-${i}`}>
                  <div className="font-display font-semibold text-white text-sm md:text-base">{t.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-[0.18em] mt-1">{t.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#0A0A0B]">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1f] to-[#0a0a0b]"></div>
              <img
                src="/images/store/hdh-welt-illustration.png"
                alt="Die ganze Welt von Haus des Handys"
                className="absolute inset-0 w-full h-full object-contain object-center"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[2rem]"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
